interface CreateNumberMaskProps {
  prefix?: string;
  suffix?: string;
  includeThousandsSeparator?: boolean;
  thousandsSeparatorSymbol?: string;
  allowDecimal?: boolean;
  decimalSymbol?: string;
  decimalLimit?: number;
  requireDecimal?: boolean;
  allowNegative?: boolean;
  allowLeadingZeroes?: boolean;
  integerLimit?: number | null;
}

const emptyString = '';
const comma = ',';
const period = '.';
const minus = '-';
const minusRegExp = /-/;
const nonDigitsRegExp = /\D+/g;
const number = 'number';
const digitRegExp = /\d/;
const caretTrap = '[]';

/**
 * Função que cria máscara numérica de acordo com as propriedades.
 *
 * Função dos [addons da 'react-text-mask'](https://github.com/text-mask/text-mask/blob/master/addons/src/createNumberMask.js)
 *
 * @export
 * @param {CreateNumberMaskProps} [{
 *   prefix = '',
 *   suffix = '',
 *   includeThousandsSeparator = true,
 *   thousandsSeparatorSymbol = comma,
 *   allowDecimal = false,
 *   decimalSymbol = period,
 *   decimalLimit = 2,
 *   requireDecimal = false,
 *   allowNegative = false,
 *   allowLeadingZeroes = false,
 *   integerLimit = null,
 * }={}]
 * @return {*}  {(rawValue: string) => string}
 */
export default function createNumberMask({
  prefix = '',
  suffix = '',
  includeThousandsSeparator = true,
  thousandsSeparatorSymbol = comma,
  allowDecimal = false,
  decimalSymbol = period,
  decimalLimit = 2,
  requireDecimal = false,
  allowNegative = false,
  allowLeadingZeroes = false,
  integerLimit = null,
}: CreateNumberMaskProps = {}): (rawValue: string) => string {
  const prefixLength = prefix?.length ?? 0;
  const suffixLength = suffix?.length ?? 0;
  const thousandsSeparatorSymbolLength = thousandsSeparatorSymbol?.length ?? 0;

  function handleMaskDecimal(
    rawValue: string,
    hasDecimal: boolean,
    indexOfLastDecimal: number,
    fraction: (string | RegExp)[],
    mask: (string | RegExp)[],
  ): (string | RegExp)[] {
    let newMask = [...mask];

    if ((hasDecimal && allowDecimal) || requireDecimal === true) {
      if (rawValue[indexOfLastDecimal - 1] !== decimalSymbol) {
        newMask.push(caretTrap);
      }

      newMask.push(decimalSymbol, caretTrap);

      if (fraction) {
        if (typeof decimalLimit === 'number') {
          fraction = fraction.slice(0, decimalLimit);
        }

        newMask = newMask.concat(fraction);
      }

      if (requireDecimal === true && rawValue[indexOfLastDecimal - 1] === decimalSymbol) {
        newMask.push(digitRegExp);
      }
    }

    return newMask;
  }

  function numberMask(rawValue = emptyString) {
    const rawValueLength = rawValue.length;

    if (
      rawValue === emptyString
      || (rawValue.startsWith(prefix[0]) && rawValueLength === 1)
    ) {
      const response: (string | RegExp[]) = prefix;
      return response.split(emptyString).concat([digitRegExp].toString()).concat(suffix.split(emptyString));
    }

    if (
      rawValue === decimalSymbol
      && allowDecimal
    ) {
      return prefix.split(emptyString).concat(['0', decimalSymbol, digitRegExp].toString()).concat(suffix.split(emptyString));
    }

    const isNegative = rawValue.startsWith(minus) && allowNegative;
    // If negative remove "-" sign
    if (isNegative) {
      rawValue = rawValue.toString().slice(1);
    }

    const indexOfLastDecimal = rawValue.lastIndexOf(decimalSymbol);
    const hasDecimal = indexOfLastDecimal !== -1;

    let integer;
    let fraction;
    let mask;

    // remove the suffix
    if (rawValue.slice(suffixLength * -1) === suffix) {
      rawValue = rawValue.slice(0, suffixLength * -1);
    }

    if (hasDecimal && (allowDecimal || requireDecimal)) {
      integer = rawValue.slice(rawValue.slice(0, prefixLength) === prefix ? prefixLength : 0, indexOfLastDecimal);

      fraction = rawValue.slice(indexOfLastDecimal + 1, rawValueLength);
      fraction = convertToMask(fraction.replace(nonDigitsRegExp, emptyString));
    } else if (rawValue.slice(0, prefixLength) === prefix) {
      integer = rawValue.slice(prefixLength);
    } else {
      integer = rawValue;
    }

    if (integerLimit && typeof integerLimit === 'number') {
      const thousandsSeparatorRegex = thousandsSeparatorSymbol === '.' ? '[.]' : `${thousandsSeparatorSymbol}`;
      const numberOfThousandSeparators = (integer.match(new RegExp(thousandsSeparatorRegex, 'g')) || []).length;

      integer = integer.slice(0, integerLimit + (numberOfThousandSeparators * thousandsSeparatorSymbolLength));
    }

    integer = integer.replace(nonDigitsRegExp, emptyString);

    if (!allowLeadingZeroes) {
      integer = integer.replace(/^0+(0$|[^0])/, '$1');
    }

    integer = (includeThousandsSeparator) ? addThousandsSeparator(integer, thousandsSeparatorSymbol) : integer;

    mask = convertToMask(integer);

    mask = handleMaskDecimal(rawValue, hasDecimal, indexOfLastDecimal, fraction, mask);

    if (prefixLength > 0) {
      mask = prefix.split(emptyString).concat(mask);
    }

    if (isNegative) {
      // If user is entering a negative number, add a mask placeholder spot to attract the caret to it.
      if (mask.length === prefixLength) {
        mask.push(digitRegExp);
      }

      mask = [minusRegExp].concat(mask);
    }

    if (suffix.length > 0) {
      mask = mask.concat(suffix.split(emptyString));
    }

    return mask;
  }

  numberMask.instanceOf = 'createNumberMask';

  return numberMask;
}

function convertToMask(strNumber: string): (string | RegExp)[] {
  return strNumber
    .split(emptyString)
    .map((char) => (digitRegExp.test(char) ? digitRegExp : char));
}

function addThousandsSeparator(n: string, thousandsSeparatorSymbol: string): string {
  return Number(n).toLocaleString('en-US').replace(/,/g, thousandsSeparatorSymbol);
}

export function MaskPhone(v) {
  v = v.replace(/\D/g, '');
  if (v.length > 11) {
    v = v.substr(0, 11);
  }
  if (v.length === 11) {
    v = v.replace(/^(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  } else {
    v = v.replace(/^(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }

  return v;
}

export function MaskCnpj(cnpj) {
  cnpj = cnpj.replace(/\D/g, '');
  if (cnpj.length > 14) {
    cnpj = cnpj.substr(0, 14);
  }
  cnpj = cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');

  return cnpj;
}

export function MaskCPF(cpf) {
  cpf = cpf.replace(/\D/g, '');
  if (cpf.length > 11) {
    cpf = cpf.substr(0, 11);
  }
  cpf = cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');

  return cpf;
}

export function maskIpAddress(): {
  guide: boolean,
  mask: (value: string) => (RegExp | string)[];
  pipe: (value: string) => boolean | string;
  } {
  const props = {
    guide: false,
    mask: (value) => {
      const mask = Array(value.length).fill(/[\d.]/);
      const chunks = value.split('.');

      for (let i = 0; i < 4; ++i) {
        const chunk = (chunks[i] || '');

        if (255 % +chunk === 255) {
          mask[value.length - 1] = '.';
          mask[value.length] = /[\d.]/;
        }
      }

      return mask;
    },
    pipe: (value) => {
      if (value === '.' || value.endsWith('..')) return false;

      const parts = value.split('.');

      if (
        parts.length > 4
      || parts.some((part) => part === '00' || part < 0 || part > 255)
      ) {
        return false;
      }

      return value;
    },
  };

  return props;
}

export const TIME_MASK = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];

export const DATE_MASK = [/[0-3]/, /\d/, '/', /[0-1]/, /\d/, '/', /2/, /0/, /\d/, /\d/];
